'use client';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { elowenClient } from "../elowenClient";
import { clearToken } from "../token";
import { QUERY_KEYS } from "../queries";
import type { UserPatch, ProfilePatch, UserPluginConfigDetail } from "../types";

/** Admin: let the selected account's monthly limit count from zero. Statistics are untouched, so only that
 *  account's spend and the directory projection are read again. */
export function useResetUserUsage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => elowenClient.resetUserUsage(userId),
    onSuccess: (_data, userId) => {
      qc.invalidateQueries({ queryKey: ['user-spend', userId] });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { username: string; password: string }) => elowenClient.login(v.username, v.password),
    // The reads that ran while logged out are still mounted above the auth gate (the skin and toast
    // config), and what they hold is the logged-out 401 — which no longer clears the session, so nothing
    // else would ever replace it. Reset drops that state, cancels what is still in flight under the dead
    // session and refetches as the new one, which is what puts the skin and the toast duration in place
    // the instant someone logs in rather than after a reload.
    retry: false,
    onSuccess: (result) => { if (result.status === 'authenticated') void qc.resetQueries(); },
  });
}

export function useCompleteTotpLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => elowenClient.completeTotpLogin(code),
    retry: false,
    onSuccess: () => { void qc.resetQueries(); },
  });
}

/** Internal to `useSignOut` — ending a session always has to clear the cookie and reload, so nothing
 *  outside this file should call the bare mutation and re-implement that half. */
function useLogout() {
  return useMutation({ mutationFn: () => elowenClient.logout() });
}

/** Ending a session, ready to hand straight to an onClick. The cookie is cleared and the page reloaded on
 *  EITHER outcome on purpose: when the daemon is already unreachable the request fails, and leaving the
 *  user inside a session the UI still believes in is the one result a logout button must never produce. */
export function useSignOut(): { signOut: () => void; isPending: boolean } {
  const logout = useLogout();
  const finish = (): void => { clearToken(); window.location.reload(); };
  return {
    signOut: () => logout.mutate(undefined, { onSuccess: finish, onError: finish }),
    isPending: logout.isPending,
  };
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { username: string; password: string; projects: import('../../../src/shared/accountProjects').AccountProjectChoice; preset_id?: number | null }) => elowenClient.createUser(v.username, v.password, v.projects, v.preset_id), onSuccess: async () => { await Promise.all([qc.invalidateQueries({ queryKey: ['users'] }), qc.invalidateQueries({ queryKey: ['projects'] }), qc.invalidateQueries({ queryKey: ['project-summaries'] }), qc.invalidateQueries({ queryKey: QUERY_KEYS.accountPresets })]); } });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: number) => elowenClient.deleteUser(id), onSuccess: async () => { await Promise.all([qc.invalidateQueries({ queryKey: ['users'] }), qc.invalidateQueries({ queryKey: ['project-summaries'] }), qc.invalidateQueries({ queryKey: QUERY_KEYS.accountPresets })]); } });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; patch: UserPatch }) => elowenClient.updateUser(v.id, v.patch),
    onSuccess: async (_user, { id, patch }) => {
      const invalidations = [
        qc.invalidateQueries({ queryKey: ['users'] }),
        qc.invalidateQueries({ queryKey: ['me'] }),
        // Names and role changes affect Project member samples and counts.
        qc.invalidateQueries({ queryKey: ['project-summaries'] }),
      ];
      // The effective tool list is derived from the plugin grant as well as the per-tool grant. Refresh
      // its one user-scoped cache whenever either input changes so the Users drawer never needs an F5.
      invalidations.push(qc.invalidateQueries({ queryKey: QUERY_KEYS.accountPresets }));
      if ('preset_id' in patch) invalidations.push(qc.invalidateQueries({ queryKey: ['user-projects', id] }));
      if ('preset_id' in patch || patch.granted_plugins || patch.allowed_tools) {
        invalidations.push(qc.invalidateQueries({ queryKey: ['user-tools', id] }));
      }
      if ('preset_id' in patch || patch.allowed_execs) invalidations.push(qc.invalidateQueries({ queryKey: ['brain-models'] }));
      await Promise.all(invalidations);
    },
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (patch: ProfilePatch) => elowenClient.updateMe(patch), onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }) });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (file: File) => elowenClient.uploadAvatar(file), onSuccess: (me) => {
    qc.invalidateQueries({ queryKey: ['me'] });
    // The new file has a new version, so the next signed link differs and the browser fetches the new bytes.
    qc.invalidateQueries({ queryKey: [...QUERY_KEYS.avatarUrl, me.id] });
  } });
}

export function useChangePassword() {
  return useMutation({ mutationFn: (v: { currentPassword: string; newPassword: string }) => elowenClient.changePassword(v.currentPassword, v.newPassword) });
}

export function useSaveUserPluginConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; values: Record<string, unknown>; expectedRevision?: number }) => elowenClient.saveUserPluginConfig(v.name, v.values, v.expectedRevision),
    onSuccess: (detail) => qc.setQueryData<UserPluginConfigDetail[]>(QUERY_KEYS.userPluginConfigs, (current) => current?.map((item) => item.name === detail.name ? detail : item)),
  });
}
